import React, { useEffect, useState, useRef, useCallback } from "react";
import Lottie from "react-lottie";
import { Container } from "react-grid-system";
import { PDFViewer as PaperviewPDF } from "@paperviewpdf/react-viewer";

import security from "../../../services/Security";
import httpClient from "../../../lib/HttpClient";
import Header from "../../shared/Header/Header";
import VersionComparePanel from "./VersionComparePanel.js";
import {
  COMPARE_MODES,
  documentKindOf,
  loadCompareMode,
  saveCompareMode,
} from "./compareMode";
import { loadDocumentUrl } from "../../../lib/axios-client";
import { waitForPdfReady } from "../../../lib/pdfReadiness";
import warning from "../../../images/icons/svg/warning-sign.svg";
import loadingAnimation from "../../../images/lotties/loading.json";
import "../../../lib/paperviewIcons";

import styles from "./CompareDocuments.module.scss";

/**
 * Builds the authenticated URL for a version's PDF rendition.
 * Mirrors PaperViewer.getDocumentUrl(): PaperView PDF only renders PDFs, so
 * office versions rely on the backend serving a `.pdf` rendition.
 *
 * @param {string|number} versionId - The document version ID.
 * @returns {string} Absolute URL including the `?token=` query parameter.
 */
const buildVersionUrl = function (versionId) {
  const token = security.getToken();
  const baseUrl = loadDocumentUrl();

  return baseUrl + "/documents/" + versionId + "/file.pdf?token=" + token;
};

/**
 * Stable PaperView PDF document ID for a version. The compare plugin requires
 * the two sides to have distinct document IDs; VersionComparePanel already
 * prevents the same version from being selected on both sides.
 *
 * @param {string|number} versionId - The document version ID.
 * @returns {string} Document ID used with the document-manager plugin.
 */
const buildDocumentId = function (versionId) {
  return "version-" + versionId;
};

/**
 * Maps the backend versions list to react-select options, flagging video
 * versions as disabled since they cannot be compared.
 *
 * @param {Array<Object>} versions - Versions from `/documents/:id/versions.json`.
 * @returns {Array<{value: number, label: string, isDisabled?: boolean}>}
 */
const versionsWithVideosFlagged = function (versions) {
  const flaggedVersions = [];

  versions.forEach(function (version) {
    const label = "Version " + version.version_number;

    if (version.is_video) {
      flaggedVersions.push({
        value: version.id,
        label: label + " - Cannot Compare Videos",
        isDisabled: true,
      });
    } else {
      flaggedVersions.push({
        value: version.id,
        label: label,
      });
    }
  });

  return flaggedVersions;
};

const loaderOptions = {
  loop: true,
  autoplay: true,
  animationData: loadingAnimation,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

/**
 * Side-by-side version compare powered by PaperView PDF's compare plugin.
 *
 * Route params `doc1_id` / `doc2_id` are *version* IDs. The left pane is the
 * "old" version and the right pane is the "new" version. Both versions are
 * opened programmatically through the document-manager plugin, then handed
 * to the compare plugin which renders the split view, synced scroll/zoom,
 * word-level diff highlights and the changes sidebar.
 *
 * This page is always opened in a new tab (see DocumentVersionsPanel and
 * DraftUpdateForm), so the built-in "Exit compare" button closes the tab.
 */
const CompareDocuments = function (props) {
  const initialVersionOne = props.match.params.doc1_id;
  const initialVersionTwo = props.match.params.doc2_id;

  const [versionsToCompare, setVersionsToCompare] = useState([
    initialVersionOne,
    initialVersionTwo,
  ]);
  const [versions, setVersions] = useState([]);
  const [showError, setShowError] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  // Bumped to give each new version pair a fresh viewer (see
  // applyVersionsToCompare).
  const [viewerInstance, setViewerInstance] = useState(0);
  // Text only, or text plus artwork (pixel compare). Null until the first
  // comparison has picked one for the pair.
  const [compareMode, setCompareMode] = useState(null);

  // Plugin capabilities captured once the viewer is ready.
  const documentManagerRef = useRef(null);
  const compareRef = useRef(null);

  // Mirrors of state that async Task callbacks need to read without being
  // subject to stale closures.
  const versionsToCompareRef = useRef(versionsToCompare);
  const showLoaderRef = useRef(true);

  // Incremented on every comparison request. A request that finishes after a
  // newer one started must not touch state or the viewer.
  const comparisonRunIdRef = useRef(0);

  // Unsubscribe handle for the compare session listener.
  const sessionUnsubscribeRef = useRef(null);

  // Cancel handles for in-flight PDF-rendition readiness waits.
  const cancelPdfReadyWaitsRef = useRef([]);

  // Version details by version (document) ID, from versions.json. Used to
  // label the compare panes with the document title — the pickers above the
  // viewer already show the sequential version numbers, and the document
  // name appears nowhere else on this page — and to pick the compare mode
  // from the versions' file types.
  const versionDetailsRef = useRef({});

  // Resolves once the version list has loaded (also on failure, so a
  // comparison can still run with fallback labels).
  const versionsLoadedPromiseRef = useRef(null);

  const updateLoader = useCallback(function (isLoading) {
    showLoaderRef.current = isLoading;
    setShowLoader(isLoading);
  }, []);

  /**
   * Display name for a compare pane: the version's document title. The
   * version pickers already show sequential version numbers, so the title
   * is what identifies the document itself. Falls back to the version
   * number, then the raw ID, if the list has not loaded.
   *
   * @param {string|number} versionId - The document version ID.
   * @returns {string}
   */
  const buildDocumentName = function (versionId) {
    const details = versionDetailsRef.current[versionId];

    if (details && details.title) {
      return details.title;
    }
    if (details && details.versionNumber) {
      return "Version " + details.versionNumber;
    }
    return "Version " + versionId;
  };

  /**
   * Whether a version pair is an Office document or anything else, from the
   * versions' file types. Before the version list has loaded it is
   * "other".
   *
   * @param {string|number} oldVersionId
   * @param {string|number} newVersionId
   * @returns {string}
   */
  const documentKindForPair = function (oldVersionId, newVersionId) {
    const details = versionDetailsRef.current;
    const mimeTypeOf = function (versionId) {
      return details[versionId] ? details[versionId].mimeType : null;
    };

    return documentKindOf([mimeTypeOf(oldVersionId), mimeTypeOf(newVersionId)]);
  };

  /**
   * Closes any open documents, opens both versions and starts a compare
   * session. Calling this again while a previous run is still loading
   * supersedes that run.
   *
   * @param {string|number} oldVersionId - Version shown in the left pane.
   * @param {string|number} newVersionId - Version shown in the right pane.
   */
  const runComparison = useCallback(
    function (oldVersionId, newVersionId) {
      const documentManager = documentManagerRef.current;
      const compare = compareRef.current;
      const hasCapabilities = documentManager && compare;

      if (!hasCapabilities) {
        return;
      }

      comparisonRunIdRef.current += 1;
      const runId = comparisonRunIdRef.current;
      const isCurrentRun = function () {
        return runId === comparisonRunIdRef.current;
      };

      const oldDocumentId = buildDocumentId(oldVersionId);
      const newDocumentId = buildDocumentId(newVersionId);

      updateLoader(true);
      setShowError(false);

      // Tear down the previous session before closing its documents. This
      // fires onSessionChange with a null pair, which is ignored while the
      // loader is showing (see handleViewerReady).
      compare.stopCompare();

      const openDocumentsAndCompare = function () {
        documentManager
          .closeAllDocuments()
          .toPromise()
          .then(function () {
            if (!isCurrentRun()) {
              return;
            }

            // openDocumentUrl resolves with { documentId, task } as soon as the
            // document is registered; the inner task resolves once the PDF is
            // fully loaded. startCompare requires both to be fully loaded.
            const openOldVersion = documentManager
              .openDocumentUrl({
                url: buildVersionUrl(oldVersionId),
                documentId: oldDocumentId,
                name: buildDocumentName(oldVersionId),
              })
              .toPromise()
              .then(function (response) {
                return response.task.toPromise();
              });

            const openNewVersion = documentManager
              .openDocumentUrl({
                url: buildVersionUrl(newVersionId),
                documentId: newDocumentId,
                name: buildDocumentName(newVersionId),
                autoActivate: false,
              })
              .toPromise()
              .then(function (response) {
                return response.task.toPromise();
              });

            return Promise.all([openOldVersion, openNewVersion]).then(
              function () {
                if (!isCurrentRun()) {
                  return;
                }

                const mode = loadCompareMode(
                  documentKindForPair(oldVersionId, newVersionId)
                );
                setCompareMode(mode);

                const compareOptions = {
                  oldDocumentId: oldDocumentId,
                  newDocumentId: newDocumentId,
                };
                if (mode === COMPARE_MODES.ARTWORK) {
                  // Also compare the rendered pages, so changes to artwork
                  // and outlined text show up, not only changes to text.
                  compareOptions.pixel = "artwork";
                }

                return compare
                  .startCompare(compareOptions)
                  .toPromise()
                  .then(function () {
                    if (isCurrentRun()) {
                      updateLoader(false);
                    }
                  });
              }
            );
          })
          .catch(function (error) {
            if (!isCurrentRun()) {
              return;
            }

            console.error("Version compare failed", error);
            updateLoader(false);
            setShowError(true);
          });
      };

      // Both versions' PDF renditions may still be generating (office
      // uploads); wait for both before opening them. The loader stays up
      // while waiting.
      cancelPdfReadyWaitsRef.current.forEach(function (cancelWait) {
        cancelWait();
      });
      cancelPdfReadyWaitsRef.current = [];

      // Three gates before opening: both renditions ready, plus the version
      // list (it supplies the pane labels).
      let versionsReady = 0;
      const handleVersionReady = function () {
        versionsReady += 1;
        if (versionsReady === 3 && isCurrentRun()) {
          openDocumentsAndCompare();
        }
      };
      const handleVersionTimeout = function () {
        if (isCurrentRun()) {
          updateLoader(false);
          setShowError(true);
        }
      };

      [oldVersionId, newVersionId].forEach(function (versionId) {
        cancelPdfReadyWaitsRef.current.push(
          waitForPdfReady({
            documentId: versionId,
            fileUrl: buildVersionUrl(versionId),
            onReady: handleVersionReady,
            onTimeout: handleVersionTimeout,
          })
        );
      });

      if (versionsLoadedPromiseRef.current) {
        versionsLoadedPromiseRef.current.then(function () {
          handleVersionReady();
        });
      } else {
        handleVersionReady();
      }
    },
    [updateLoader]
  );

  /**
   * Captures the plugin capabilities once PaperView PDF has initialised,
   * wires the exit-compare behaviour and starts the initial comparison.
   *
   * @param {Object} registry - PaperView PDF plugin registry.
   */
  const handleViewerReady = useCallback(
    function (registry) {
      const documentManagerProvider = registry.getPlugin("document-manager");
      const compareProvider = registry.getPlugin("compare");

      if (documentManagerProvider) {
        documentManagerRef.current = documentManagerProvider.provides();
      }
      if (compareProvider) {
        compareRef.current = compareProvider.provides();
      }

      const hasCapabilities = documentManagerRef.current && compareRef.current;

      if (!hasCapabilities) {
        console.error(
          "PaperView PDF document-manager or compare plugin is unavailable"
        );
        updateLoader(false);
        setShowError(true);
        return;
      }

      // The viewer's built-in "Exit compare" button ends the session, which
      // surfaces here as a null pair. Re-comparisons also briefly end the
      // session via stopCompare(), so only close the tab when the user did it
      // (i.e. when no comparison is loading). window.close() is a no-op for
      // tabs the user opened directly, which leaves the viewer showing both
      // versions without highlights.
      sessionUnsubscribeRef.current = compareRef.current.onSessionChange(
        function (session) {
          const userExitedCompare = !session.pair && !showLoaderRef.current;

          if (userExitedCompare) {
            window.close();
          }
        }
      );

      const currentVersions = versionsToCompareRef.current;
      runComparison(currentVersions[0], currentVersions[1]);
    },
    [runComparison, updateLoader]
  );

  // Unsubscribe from the compare session and end it when leaving the page.
  useEffect(function () {
    return function () {
      cancelPdfReadyWaitsRef.current.forEach(function (cancelWait) {
        cancelWait();
      });
      cancelPdfReadyWaitsRef.current = [];

      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
      if (compareRef.current) {
        compareRef.current.stopCompare();
      }
    };
  }, []);

  // Load the version list for the pickers. The list belongs to the parent
  // document, so any version ID works as the lookup key.
  useEffect(
    function () {
      versionsLoadedPromiseRef.current = httpClient
        .get("/documents/" + initialVersionOne + "/versions.json")
        .then(function (response) {
          const detailsById = {};
          response.data.forEach(function (version) {
            detailsById[version.id] = {
              title: version.title,
              versionNumber: version.version_number,
              mimeType: version.mime_type,
            };
          });
          versionDetailsRef.current = detailsById;

          setVersions(versionsWithVideosFlagged(response.data));
        })
        .catch(function (error) {
          console.error("Failed to load versions for compare", error);
          setShowError(true);
        });
    },
    [initialVersionOne]
  );

  /**
   * Applies a new version pair: updates state and the address bar, then
   * re-runs the comparison without reloading the page.
   *
   * @param {string|number} oldVersionId - Version for the left pane.
   * @param {string|number} newVersionId - Version for the right pane.
   */
  const applyVersionsToCompare = function (oldVersionId, newVersionId) {
    const nextPair = [oldVersionId, newVersionId];

    versionsToCompareRef.current = nextPair;
    setVersionsToCompare(nextPair);

    window.history.pushState(
      null,
      "",
      "/compare/" + oldVersionId + "/" + newVersionId
    );

    restartInFreshViewer();
  };

  /**
   * Re-runs the comparison of the current pair in a fresh viewer.
   */
  const restartInFreshViewer = function () {
    // Start over in a fresh viewer rather than closing and reopening
    // documents in the one that is mounted. Ending a session there shows a
    // single-document pane for a moment, and if its document closes before
    // the pane has finished setting up, the viewer throws ("Interaction
    // state not found…") and stays blank. A new viewer has nothing to close.
    // handleViewerReady runs the comparison once it is up.
    comparisonRunIdRef.current += 1;
    if (sessionUnsubscribeRef.current) {
      sessionUnsubscribeRef.current();
      sessionUnsubscribeRef.current = null;
    }
    documentManagerRef.current = null;
    compareRef.current = null;
    updateLoader(true);
    setShowError(false);
    setViewerInstance(function (instance) {
      return instance + 1;
    });
  };

  const changeVersionsToCompare = function (versionOne, versionTwo) {
    applyVersionsToCompare(versionOne, versionTwo);
  };

  /**
   * Switches between text only and text plus artwork, remembers the choice
   * for this kind of document and compares the pair again.
   *
   * @param {string} mode - a COMPARE_MODES value
   */
  const changeCompareMode = function (mode) {
    if (mode === compareMode) {
      return;
    }

    const currentVersions = versionsToCompareRef.current;
    saveCompareMode(
      documentKindForPair(currentVersions[0], currentVersions[1]),
      mode
    );
    setCompareMode(mode);
    restartInFreshViewer();
  };

  const swapComparisonOrder = function () {
    const currentVersions = versionsToCompareRef.current;
    applyVersionsToCompare(currentVersions[1], currentVersions[0]);
  };

  return (
    <Container fluid className={styles.documentViewer}>
      <div className={styles.headerContainer}>
        <Header />
      </div>
      <VersionComparePanel
        changeVersionsToCompare={changeVersionsToCompare}
        swapComparisonOrder={swapComparisonOrder}
        versions={versions}
        v1={versionsToCompare[0]}
        v2={versionsToCompare[1]}
        error={showError}
        compareMode={compareMode}
        changeCompareMode={changeCompareMode}
        isComparing={showLoader}
      />
      <div className={styles.webViewerContainer} id="webViewerContainer">
        <div id="webViewer" className={styles.webViewer}>
          <PaperviewPDF
            key={viewerInstance}
            config={{
              // No `src`: both versions are opened in handleViewerReady.
              wasmUrl: "/pdfium.wasm",
              worker: true,
              theme: {
                preference: "light",
              },
              // The two versions would otherwise appear as document tabs
              // before the session starts; the compare layout replaces them.
              tabBar: "never",
              // Read-only compare: no annotation/redaction tooling, no
              // comment panel, no print/export. Compare categories stay on.
              disabledCategories: [
                "annotation",
                "redaction",
                "mode-annotate",
                "mode-shapes",
                "mode-redact",
                "panel-comment",
                "document-print",
                "document-export",
              ],
            }}
            onReady={handleViewerReady}
            style={{ width: "100%", height: "100%" }}
          />
          {showError && (
            <div className={styles.errorStateContainer}>
              <div className={styles.errorState}>
                <img src={warning} alt="" />
                <p className={styles.warningLineOne}>
                  Oops, there was a problem
                </p>
                <p className={styles.warningLineTwo}>
                  There was an issue loading a version, please try again.
                </p>
              </div>
            </div>
          )}
          {showLoader && (
            <div className={styles.loaderContainer}>
              <Lottie options={loaderOptions} height={92} width={82} />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default CompareDocuments;
